import React, { useState, useEffect, useRef } from 'react';
import SearchableDropdown from '../common/SearchableDropdown';
import { US_STATES, US_CITIES } from '../../data/locationData';
import { useDispatch, useSelector } from 'react-redux';
import { updateLead, uploadLeadAttachments, deleteLeadAttachment, deleteLead, fetchCategories, fetchLeadById } from '../../store/slices/leadsSlice';
import { showToast } from '../utils/Toast';
import EmailTagInput from '../common/EmailTagInput';
import { 
  fetchProposals, 
  deleteProposal, 
  releaseProposal,
  updateProposalTitle,
  duplicateProposal
} from '../../store/slices/proposalsSlice';
import ProposalsTable from './ProposalsTable';
import MultiSelectDropdown from '../common/MultiSelectDropdown';
import { fetchClients } from '../../store/slices/clientsSlice';
import { fetchCities } from '../../store/slices/citiesSlice';

// Import the new components that were extracted
import ClientSearchSelect from './ClientSearchSelect';
import ImagePreviewDialog from './ImagePreviewDialog';
import CreateProposalDialog from './CreateProposalDialog';
import PermitsTab from '../permits/PermitsTab';
import { fetchProjects } from '../../store/slices/projectsSlice';
import { printElement } from '../../utils/PrintUtils';
import { getCorrectFileUrl, isImageFile, isPdfFile } from '../utils/fileUtils';

// The main LeadDetailsDialog component stays the same, but uses the imported components where needed
// instead of having them all defined in the same file

const LeadDetailsDialog = ({ isOpen, onClose, lead }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('General');
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedManagers, setSelectedManagers] = useState(lead?.projectManagers || []);
  const [previewImage, setPreviewImage] = useState(null);
  const [showCreateProposal, setShowCreateProposal] = useState(false);
  const { proposals = [], loading = false } = useSelector(state => state.proposals || {});
  // Add clients from the Redux store
  const { clients = [] } = useSelector(state => state.clients || {});
  // Permits live on the converted Project. Look up the project linked to this lead
  // (if any) so we can show the Permits tab in the Lead view too.
  const { projects: allProjects = [] } = useSelector(state => state.projects || {});
  const linkedProject = React.useMemo(() => {
    if (!lead?._id) return null;
    return (allProjects || []).find((p) => {
      const orig = p?.originalLead;
      if (!orig) return false;
      const origId = typeof orig === 'object' ? orig._id : orig;
      return origId && origId.toString() === lead._id.toString();
    }) || null;
  }, [allProjects, lead?._id]);
  const [editingProposal, setEditingProposal] = useState(null);
  const [showDeleteProposalConfirm, setShowDeleteProposalConfirm] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [isMaximized, setIsMaximized] = useState(true);
  const [isTabbedView, setIsTabbedView] = useState(false);
  // Add new state variables for copying client info
  const [sameBillingAsClient, setSameBillingAsClient] = useState(false);
  const [sameSiteAsClient, setSameSiteAsClient] = useState(false);
  
  // Add drag and drop functionality variables
  const generalTabDropAreaRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };
  
  const handleFiles = async (files) => {
    if (!files.length) return;
    
    try {
      showToast('Uploading documents...', 'info');
      
      const result = await dispatch(uploadLeadAttachments({
        leadId: lead._id,
        files: Array.from(files)
      })).unwrap();
      
      if (result.lead && result.lead.attachments) {
        setFormData(prev => ({
          ...prev,
          attachments: result.lead.attachments
        }));
      }
      
      showToast('Documents uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading files:', error);
      showToast(error.message || 'Error uploading files', 'error');
    }
  };
  
  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    // Reset the input value so selecting the same file again still fires
    // onChange (otherwise re-uploading the same file does nothing).
    e.target.value = '';
  };
  
  // Initialize form data from lead prop
  const [formData, setFormData] = useState({
    leadContact: {
      name: '',
      phone: '',
      email: ''
    },
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    client: {
      name: '',
      email: '',
      emails: [], // Array for EmailTagInput
      cellPhone: '',
      altPhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    },
    billingContact: {
      name: '',
      phone: '',
      email: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    },
    siteContact: {
      name: '',
      email: '',
      cellPhone: '',
      altPhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    },
    notes: '',
    mapLink: '',
    attachments: []
  });

  // Add cities from Redux store
  const cities = useSelector(state => state.cities?.items || []);
  const citiesLoading = useSelector(state => state.cities?.loading);
  const citiesError = useSelector(state => state.cities?.error);

  // Update form data when lead prop changes
  useEffect(() => {
    if (lead) {
      console.log('Lead data in dialog:', lead);
      
      setFormData({
        leadContact: {
          name: lead.leadContact?.name || '',
          phone: lead.leadContact?.phone || '',
          email: lead.leadContact?.email || ''
        },
        address: {
          street: lead.address?.street || '',
          city: lead.address?.city || '',
          state: lead.address?.state || '',
          zipCode: lead.address?.zipCode || ''
        },
        client: {
          name: lead.client?.name || '',
          email: lead.client?.email || '',
          emails: lead.client?.email ? lead.client.email.split(',').map(email => email.trim()).filter(Boolean) : [],
          cellPhone: lead.client?.cellPhone || '',
          altPhone: lead.client?.altPhone || '',
          address: {
            street: lead.client?.address?.street || '',
            city: lead.client?.address?.city || '',
            state: lead.client?.address?.state || '',
            zipCode: lead.client?.address?.zipCode || ''
          }
        },
        billingContact: {
          name: lead.billingContact?.name || '',
          phone: lead.billingContact?.phone || '',
          email: lead.billingContact?.email || '',
          address: {
            street: lead.billingContact?.address?.street || '',
            city: lead.billingContact?.address?.city || '',
            state: lead.billingContact?.address?.state || '',
            zipCode: lead.billingContact?.address?.zipCode || ''
          }
        },
        siteContact: {
          name: lead.siteContact?.name || '',
          email: lead.siteContact?.email || '',
          cellPhone: lead.siteContact?.cellPhone || '',
          altPhone: lead.siteContact?.altPhone || '',
          address: {
            street: lead.siteContact?.address?.street || '',
            city: lead.siteContact?.address?.city || '',
            state: lead.siteContact?.address?.state || '',
            zipCode: lead.siteContact?.address?.zipCode || ''
          }
        },
        categoryId: lead.categoryId || '',
        notes: lead.notes?.[0]?.content || '',
        mapLink: lead.mapLink || '',
        attachments: lead.attachments || [],
        jobNumber: lead.jobNumber || '',
        leadStatus: lead.leadStatus || '',
        priority: lead.priority || '',
        isArchived: lead.isArchived || false
      });

      // Log the attachments for debugging
      console.log('Attachments loaded:', lead.attachments);

      // Set project managers
      setSelectedManagers(lead.projectManagers || []);
      
      // Reset to first step when opening new lead
      setCurrentStep(1);
    }
    // Only re-initialize local form state when a *different* lead is opened or
    // the dialog is (re)opened. Depending on the whole `lead` object caused
    // freshly uploaded attachments (set from the upload response) to be
    // overwritten whenever the parent passed a new-but-stale `lead` reference
    // after an upload. `isOpen` does not change during an upload, so it is safe
    // here and keeps the dialog fresh on reopen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?._id, isOpen]);

  // The leads list response omits attachments/notes/categoryId for performance,
  // so the `lead` prop (taken from that list) can be missing them — which made
  // uploaded files appear to vanish after a page refresh. Fetch the full lead
  // when the dialog opens and fill in those fields.
  useEffect(() => {
    if (!isOpen || !lead?._id) return;
    dispatch(fetchLeadById(lead._id))
      .unwrap()
      .then((data) => {
        const full = data?.lead || data;
        if (!full) return;
        setFormData(prev => ({
          ...prev,
          attachments: full.attachments || prev.attachments,
          notes: full.notes?.[0]?.content ?? prev.notes,
          categoryId: full.categoryId ?? prev.categoryId
        }));
      })
      .catch((error) => {
        console.error('Error fetching full lead details:', error);
      });
  }, [isOpen, lead?._id, dispatch]);

  // Add this useEffect to fetch proposals when the tab changes or lead changes
  useEffect(() => {
    if (activeTab === 'Proposals' && lead?._id) {
      dispatch(fetchProposals(lead._id));
    }
  }, [activeTab, lead?._id, dispatch]);

  // When the user opens the Permits tab and we don't yet have projects in state,
  // pull them so we can resolve the lead → project link.
  useEffect(() => {
    if (activeTab === 'Permits' && lead?._id && (!allProjects || allProjects.length === 0)) {
      dispatch(fetchProjects({ limit: 999999 }));
    }
  }, [activeTab, lead?._id, allProjects, dispatch]);

  const [editableFields, setEditableFields] = useState({
    city: false,
    state: false,
    clientCity: false,
    clientState: false,
    siteContactCity: false,
    siteContactState: false
  });

  const handleManagerSelect = (manager) => {
    setSelectedManagers(prev => [...prev, manager]);
  };

  const handleManagerRemove = (managerId) => {
    setSelectedManagers(prev => prev.filter(manager => manager._id !== managerId));
  };

  // Update the handleSave function to include billingContact
  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.leadContact.name) {
        showToast('Lead/Client Name is required', 'error');
        return;
      }

      if (!formData.leadContact.email) {
        showToast('Client Email is required', 'error');
        return;
      }

      if (!formData.address.city) {
        showToast('City is required', 'error');
        return;
      }

      if (!selectedManagers.length) {
        showToast('At least one Project Manager is required', 'error');
        return;
      }

      if (!formData.siteContact.address.street) {
        showToast('Job Address is required', 'error');
        return;
      }

      if (!formData.client.emails || formData.client.emails.length === 0) {
        showToast('Client Email is required', 'error');
        return;
      }

      // Create the updates object with all form data
      const updates = {
        // Lead Contact Info
        leadContact: {
          name: formData.leadContact.name,
          phone: formData.leadContact.phone,
          email: formData.leadContact.email
        },
        // Address Info
        address: {
          street: formData.address.street,
          city: formData.address.city,
          state: formData.address.state,
          zipCode: formData.address.zipCode
        },
        // Client Info - always include client data
        client: formData.client._id ? 
          // If we have an ID, just send that
          formData.client._id : 
          // Otherwise send all the client data
          {
            name: formData.client.name,
            email: formData.client.emails.join(', '), // Join array to string for API
            cellPhone: formData.client.cellPhone,
            altPhone: formData.client.altPhone,
            address: {
              street: formData.client.address.street,
              city: formData.client.address.city,
              state: formData.client.address.state,
              zipCode: formData.client.address.zipCode
            }
          },
        // Billing Contact Info
        billingContact: {
          name: formData.billingContact.name,
          phone: formData.billingContact.phone,
          email: formData.billingContact.email,
          address: {
            street: formData.billingContact.address.street,
            city: formData.billingContact.address.city,
            state: formData.billingContact.address.state,
            zipCode: formData.billingContact.address.zipCode
            }
          },
        // Site Contact Info
        siteContact: {
          name: formData.siteContact.name,
          email: formData.siteContact.email,
          cellPhone: formData.siteContact.cellPhone,
          altPhone: formData.siteContact.altPhone,
          address: {
            street: formData.siteContact.address.street,
            city: formData.siteContact.address.city,
            state: formData.siteContact.address.state,
            zipCode: formData.siteContact.address.zipCode
          }
        },
        // Project Managers
        projectManagers: selectedManagers.map(manager => manager._id),
        
        // Category
        categoryId: formData.categoryId,
        
        // Notes
        notes: formData.notes ? [{
          content: formData.notes,
          createdAt: new Date()
        }] : [],
        
        // Other fields
        mapLink: formData.mapLink,
        jobNumber: formData.jobNumber,
        leadStatus: formData.leadStatus,
        priority: formData.priority,
        isArchived: formData.isArchived
      };

      // Log the updates being sent to the API
      console.log('Sending updates to API:', updates);
      console.log('Client data being sent:', updates.client);

      // Send the update request
      await dispatch(updateLead({ id: lead._id, updates })).unwrap();
      showToast('Lead updated successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Failed to save lead:', error);
      showToast('Failed to save lead', 'error');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await dispatch(deleteLeadAttachment({
        leadId: lead._id,
        attachmentId
      })).unwrap();

      // Update local state
      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter(att => att._id !== attachmentId)
      }));
    } catch (error) {
      console.error('Error deleting attachment:', error);
      showToast(error.message || 'Error deleting attachment', 'error');
    }
  };

  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white
  `;

  const textAreaClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white resize-none h-24
  `;

  const labelClasses = "block text-xs uppercase text-gray-500 mb-1";

  // Add RequiredLabel component
  const RequiredLabel = ({ children }) => (
    <div className={labelClasses}>
      <div className="flex items-center gap-2">
        <span>{children}</span>
        <span className="text-red-500">*</span>
      </div>
    </div>
  );

  // Replace the renderStepIndicator function with this new tabbed version
  const renderStepIndicator = () => {
    const steps = [
      { number: 1, label: 'Lead Info' },
      { number: 2, label: 'Client Info' },
      { number: 3, label: 'Project/Contact Info' },
      { number: 4, label: 'Billing Info' },
      { number: 5, label: 'Notes & Files' }
    ];
    
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center">
          {steps.map((step) => (
              <button
              key={step.number}
              className={`flex flex-col items-center space-y-1 ${currentStep === step.number ? 'text-blue-600' : 'text-gray-400'}`}
              onClick={() => setCurrentStep(step.number)}
            >
              <div className={`rounded-full h-8 w-8 flex items-center justify-center 
                ${currentStep === step.number 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-600'}`}
              >
                {step.number}
              </div>
              <span className="text-xs">{step.label}</span>
              </button>
            ))}
        </div>
        <div className="mt-2 grid grid-cols-4 gap-0">
          <div className="h-1 bg-blue-600"></div>
          <div className={`h-1 ${currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
          <div className={`h-1 ${currentStep >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
          <div className={`h-1 ${currentStep >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
        </div>
      </div>
    );
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  // Add delete handler
  const handleDelete = async () => {
    try {
      await dispatch(deleteLead(lead._id)).unwrap();
      showToast('Lead deleted successfully', 'success');
      setShowDeleteConfirm(false); // Close the confirmation dialog first
      onClose(); // Then close the main dialog
    } catch (error) {
      console.error('Failed to delete lead:', error);
      showToast('Failed to delete lead', 'error');
      setShowDeleteConfirm(false); // Close the confirmation dialog on error too
    }
  };

  // Add state for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleEditDraft = (proposal) => {
    setEditingProposal(proposal);
    setShowCreateProposal(true);
  };

  // Add this new handler
  const handleDeleteProposal = async (proposal) => {
    setProposalToDelete(proposal);
    setShowDeleteProposalConfirm(true);
  };

  // Add this new handler
  const confirmDeleteProposal = async () => {
    try {
      await dispatch(deleteProposal(proposalToDelete._id)).unwrap();
      setShowDeleteProposalConfirm(false);
      setProposalToDelete(null);
      // Refresh proposals list
      if (lead?._id) {
        dispatch(fetchProposals(lead._id));
      }
    } catch (error) {
      console.error('Failed to delete proposal:', error);
      showToast('Failed to delete proposal', 'error');
    }
  };

  // Add this function to handle proposal row click
  const handleProposalRowClick = (proposal) => {
    setEditingProposal(proposal);
    setShowCreateProposal(true);
  };

  // Update the handler function
  const handleReleaseProposal = async (proposal) => {
    try {
      if (!proposal?._id) {
        showToast('Invalid proposal', 'error');
        return;
      }

      await dispatch(releaseProposal(proposal._id)).unwrap();
      
      // Refresh proposals list
      if (lead?._id) {
        dispatch(fetchProposals(lead._id));
      }
    } catch (error) {
      console.error('Failed to release proposal:', error);
      showToast('Failed to release proposal', 'error');
    }
  };
  
  // Add handler for duplicating a proposal
  const handleDuplicateProposal = async (proposal) => {
    try {
      if (!proposal?._id) {
        showToast('Invalid proposal', 'error');
        return;
      }

      await dispatch(duplicateProposal(proposal._id)).unwrap();
      
      // Refresh proposals list
      if (lead?._id) {
        dispatch(fetchProposals(lead._id));
      }
    } catch (error) {
      console.error('Failed to duplicate proposal:', error);
      showToast('Failed to duplicate proposal', 'error');
    }
  };

  // Add handler for updating proposal title
  const handleUpdateProposalTitle = async (proposalId, newTitle) => {
    try {
      await dispatch(updateProposalTitle({ 
        id: proposalId, 
        title: newTitle 
      })).unwrap();
      
      // Refresh proposals list to ensure consistency
      if (lead?._id) {
        dispatch(fetchProposals(lead._id));
      }
    } catch (error) {
      console.error('Failed to update proposal title:', error);
      showToast('Failed to update proposal title', 'error');
      throw error; // Re-throw to let the component handle the error state
    }
  };

  // In the renderProposalsTab function, separate the Create New Proposal button handler
  const handleCreateNewProposal = () => {
    setEditingProposal(null); // Reset any editing proposal
    setShowCreateProposal(true);
  };

  // Add utility function to decode HTML entities
  const decodeHtmlEntities = (str) => {
    if (!str) return str;
    
    // Create a temporary DOM element to decode HTML entities
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = str;
    return tempDiv.textContent || tempDiv.innerText || str;
  };

  // Add this new function to handle printing all proposals
  const handlePrintAllProposals = () => {
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <h1 class="text-2xl font-bold mb-6">Proposals for ${lead.clientName || 'Client'}</h1>
      <table class="min-w-full">
        <thead>
          <tr>
            <th>Title</th>
            <th>Category</th>
            <th>Status</th>
            <th>Client Price</th>
            <th>Created Date</th>
          </tr>
        </thead>
        <tbody>
          ${proposals.map(proposal => `
            <tr>
              <td>${decodeHtmlEntities(proposal.title) || ''}</td>
              <td>${proposal.category?.name || ''}</td>
              <td>${proposal.status || ''}</td>
              <td>$${proposal.clientPrice?.toFixed(2) || '0.00'}</td>
              <td>${new Date(proposal.createdAt).toLocaleString('en-US', {
              timeZone: 'America/Los_Angeles',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            })}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
    
    printElement(printContent);
  };

  // Update the renderProposalsTab function
  const renderProposalsTab = () => {
      return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mb-4">
          <h3 className="text-lg font-medium">Proposals</h3>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={handlePrintAllProposals}
            >
              Print
            </button>
            <button
              onClick={handleCreateNewProposal}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Create Proposal
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <ProposalsTable
            proposals={proposals}
            loading={loading}
            onRowClick={handleProposalRowClick}
            onEditDraft={handleEditDraft}
            onDeleteProposal={handleDeleteProposal}
            onReleaseProposal={handleReleaseProposal}
            onUpdateProposalTitle={handleUpdateProposalTitle}
            onDuplicateProposal={handleDuplicateProposal}
          />
          </div>
        </div>
      );
  };

  // Update the navigation buttons to be responsive
  const renderNavigationButtons = () => (
    <div className="flex flex-col sm:flex-row justify-between gap-2 mt-8">
      <div>
        {currentStep > 1 && (
          <button
            type="button"
            onClick={handleBack}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Previous
          </button>
        )}
        </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={handleNext}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          {currentStep === 5 ? 'Save' : 'Next'}
        </button>
        {currentStep === 5 && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50"
          >
            Delete lead
          </button>
        )}
      </div>
      </div>
    );

  // Update the General tab content section
  const renderGeneralTab = () => {
    if (isTabbedView) {
        return (
        <>
          {renderStepIndicator()}
          <div className="mb-8">
            {renderStepContent()}
          </div>
          {renderNavigationButtons()}
        </>
      );
    } else {
      // Non-tabbed view - show all content at once
      return (
        <div className="space-y-10">
          {/* Lead Information Section */}
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">NEW LEAD</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>Job Name</RequiredLabel>
                <input
                  type="text"
                  className={`${inputClasses} ${!formData.leadContact.name ? 'border-red-300' : ''}`}
                  placeholder="Enter job name"
                  value={formData.leadContact.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    leadContact: {
                      ...prev.leadContact,
                      name: e.target.value
                    }
                  }))}
                />
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>Job Address</RequiredLabel>
                <textarea
                  className={`${textAreaClasses} ${!formData.siteContact.address.street ? 'border-red-300' : ''}`}
                  placeholder="Enter job address"
                  value={formData.siteContact.address.street}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    siteContact: {
                      ...prev.siteContact,
                      address: {
                        ...prev.siteContact.address,
                        street: e.target.value
                      }
                    }
                  }))}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>City</RequiredLabel>
                <SearchableDropdown
                  options={cityOptions}
                  value={cityOptions.find(opt => opt.name === formData.address.city) || ''}
                  onChange={handleLeadCityChange}
                  getOptionLabel={(option) => option.label || ''}
                  placeholder="Search city..."
                  className={`${inputClasses} ${!formData.address.city ? 'border-red-300' : ''}`}
                />
              </div>
            </div>
          </div>

          {/* Client Information Section */}
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">CLIENT INFORMATION</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {/* Add Client Selector Dropdown */}
              <div className="col-span-1 md:col-span-2 mb-2">
                <label className={labelClasses}>Choose from existing client</label>
                <ClientSearchSelect
                  clients={clients}
                  value={formData.client?._id || lead?.client?._id || ''}
                  onChange={handleClientSelect}
                  className={inputClasses}
                />
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>Client Name</RequiredLabel>
                <input
                  type="text"
                  className={`${inputClasses} ${!formData.client.name ? 'border-red-300' : ''}`}
                  placeholder="Enter client name"
                  value={formData.client.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    client: {
                      ...prev.client,
                      name: e.target.value
                    }
                  }))}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className={labelClasses}>Client Address</label>
                <textarea
                  className={textAreaClasses}
                  placeholder="Enter client address"
                  value={formData.client.address.street}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    client: {
                      ...prev.client,
                      address: {
                        ...prev.client.address,
                        street: e.target.value
                      }
                    }
                  }))}
                />
              </div>

              <div>
                <label className={labelClasses}>Client Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.client.cellPhone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    client: {
                      ...prev.client,
                      cellPhone: e.target.value
                    }
                  }))}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center justify-between">
                  <RequiredLabel>Client Email</RequiredLabel>
                  <span className="text-xs text-gray-500">Type email and press Enter or comma to add</span>
                </div>
                <EmailTagInput
                  emails={formData.client.emails}
                  onChange={(emails) => {
                    const emailsJoined = emails.join(', ');
                    setFormData(prev => ({
                      ...prev,
                      client: {
                        ...prev.client,
                        emails: emails,
                        email: emailsJoined // Keep email field in sync for backward compatibility
                      },
                      // Also update leadContact email to keep it in sync
                      leadContact: {
                        ...prev.leadContact,
                        email: emailsJoined
                      }
                    }));
                  }}
                  placeholder="Enter email address(es)"
                  required
                  className={!formData.client.emails.length ? 'border-red-300' : ''}
                />
              </div>
            </div>
          </div>

          {/* Billing Information Section */}
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">BILLING INFORMATION</h2>
            
            <div className="mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="same-billing-as-client-full"
                  checked={sameBillingAsClient}
                  onChange={() => setSameBillingAsClient(!sameBillingAsClient)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="same-billing-as-client-full" className="ml-2 text-sm text-gray-700">
                  Same as client information
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="col-span-1 md:col-span-2">
                <label className={labelClasses}>Billing Name</label>
                <input
                  type="text"
                  className={inputClasses}
                  placeholder="Enter billing name"
                  value={formData.billingContact.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billingContact: {
                      ...prev.billingContact,
                      name: e.target.value
                    }
                  }))}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className={labelClasses}>Billing Address</label>
                <textarea
                  className={textAreaClasses}
                  placeholder="Enter billing address"
                  value={formData.billingContact.address.street}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billingContact: {
                      ...prev.billingContact,
                      address: {
                        ...prev.billingContact.address,
                        street: e.target.value
                      }
                    }
                  }))}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Billing Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.billingContact.phone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billingContact: {
                      ...prev.billingContact,
                      phone: e.target.value
                    }
                  }))}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Billing Email Address</label>
                <input
                  type="email"
                  className={inputClasses}
                  placeholder="Enter billing email address"
                  value={formData.billingContact.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billingContact: {
                      ...prev.billingContact,
                      email: e.target.value
                    }
                  }))}
                  disabled={sameBillingAsClient}
                />
              </div>
            </div>
          </div>

          {/* Project/Contact Info Section */}
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">PROJECT/CONTACT INFO</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              
              <div className="mb-4 md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="same-site-as-client-full"
                    checked={sameSiteAsClient}
                    onChange={() => setSameSiteAsClient(!sameSiteAsClient)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="same-site-as-client-full" className="ml-2 text-sm text-gray-700">
                    Same as client information
                  </label>
                </div>
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <label className={labelClasses}>Contact Name</label>
                <input
                  type="text"
                  className={inputClasses}
                  placeholder="Enter contact name"
                  value={formData.siteContact.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    siteContact: {
                      ...prev.siteContact,
                      name: e.target.value
                    }
                  }))}
                  disabled={sameSiteAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Contact Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.siteContact.cellPhone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    siteContact: {
                      ...prev.siteContact,
                      cellPhone: e.target.value
                    }
                  }))}
                  disabled={sameSiteAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Contact Email</label>
                <input
                  type="email"
                  className={inputClasses}
                  placeholder="Enter email address"
                  value={formData.siteContact.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    siteContact: {
                      ...prev.siteContact,
                      email: e.target.value
                    },
                    // Also update leadContact email to keep it in sync
                    leadContact: {
                      ...prev.leadContact,
                      email: e.target.value
                    }
                  }))}
                  disabled={sameSiteAsClient}
                />
              </div>
            </div>
          </div>

          {/* Project Manager Section */}
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">PROJECT MANAGER(S)</h2>
            <div className="col-span-1 md:col-span-2">
              <RequiredLabel>Project Manager(s)</RequiredLabel>
              <MultiSelectDropdown
                selectedManagers={selectedManagers}
                onManagerSelect={handleManagerSelect}
                onManagerRemove={handleManagerRemove}
              />
              {selectedManagers.length === 0 && 
                <p className="text-red-500 text-xs mt-1">Project manager is required</p>
              }
            </div>
          </div>
          
          {/* Notes Section */}
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">NOTES</h2>
            <textarea
              className={`${textAreaClasses} h-32`}
              placeholder="Enter any additional notes..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                notes: e.target.value
              }))}
            />
          </div>

          {/* Attachments Section */}
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">Attachments</h2>
            
            {/* Show existing attachments if any */}
            {formData.attachments && formData.attachments.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Attachments:</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {formData.attachments.map((attachment) => (
                    renderAttachment(attachment)
                  ))}
                </div>
              </div>
            )}

            {/* Upload area with drag and drop */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add new attachments
              </label>
              <div 
                ref={generalTabDropAreaRef}
                className={`border-2 border-dashed ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} rounded-lg p-6 relative`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload-general-tab"
                />
                <label htmlFor="file-upload-general-tab" className="cursor-pointer flex flex-col items-center justify-center absolute inset-0 p-6">
                  <svg className={`w-12 h-12 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="mt-2 text-sm text-gray-600">{isDragging ? 'Drop files here' : 'Drag and drop files here or click to browse'}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end mt-6">
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none"
            >
              Save Changes
            </button>
            </div>
          </div>
        );
    }
  };

  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Toggle between tabbed and non-tabbed view
  const toggleView = () => {
    setIsTabbedView(!isTabbedView);
  };

  // Add this useEffect to fetch clients when the dialog opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchClients({ limit: 1000 })); // Fetch all clients
    }
  }, [isOpen, dispatch]);

  // Add a function to handle client selection
  const handleClientSelect = (clientId) => {
    if (!clientId) return;
    
    const selectedClient = clients.find(client => client._id === clientId);
    if (selectedClient) {
      // Parse email string into array
      const emailString = selectedClient.email || '';
      const emailArray = emailString.split(',').map(email => email.trim()).filter(Boolean);
      
      setFormData(prev => {
        // Create updated form data with client info
        const updatedFormData = {
          ...prev,
          client: {
            _id: selectedClient._id,
            name: selectedClient.name || '',
            email: selectedClient.email || '',
            emails: emailArray,
            cellPhone: selectedClient.cellPhone || '',
            altPhone: selectedClient.altPhone || '',
            address: {
              street: selectedClient.address?.street || '',
              city: selectedClient.address?.city || '',
              state: selectedClient.address?.state || '',
              zipCode: selectedClient.address?.zipCode || ''
            }
          },
          // Also update leadContact email to keep it in sync
          leadContact: {
            ...prev.leadContact,
            email: selectedClient.email || ''
          }
        };

        // If billing checkbox is checked, copy client info to billing
        if (sameBillingAsClient) {
          updatedFormData.billingContact = {
            ...prev.billingContact,
            name: selectedClient.name || '',
            phone: selectedClient.cellPhone || '',
            email: selectedClient.email || '',
            address: {
              street: selectedClient.address?.street || '',
              city: selectedClient.address?.city || '',
              state: selectedClient.address?.state || '',
              zipCode: selectedClient.address?.zipCode || ''
            }
          };
        }
        
        // If site contact checkbox is checked, copy client info to site contact
        // BUT NOT ADDRESS FIELDS to preserve the job address
        if (sameSiteAsClient) {
          updatedFormData.siteContact = {
            ...prev.siteContact,
            name: selectedClient.name || '',
            cellPhone: selectedClient.cellPhone || '',
            altPhone: selectedClient.altPhone || '',
            email: selectedClient.email || '',
            // Deliberately NOT updating address fields to preserve job address
          };
        }
        
        return updatedFormData;
      });
    }
  };

  // Inside the LeadDetailsDialog component, add this selector after the clients selector
  const { categories = [] } = useSelector(state => state.leads || {});

  // Add useEffect to fetch categories when component mounts
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCategories());
    }
  }, [isOpen, dispatch]);

  // Add renderAttachment function to display individual attachments
  const renderAttachment = (attachment) => {
    // Check if the attachment has a file type to determine if it's an image
    const isImage = attachment.fileType 
      ? isImageFile(attachment.fileName, attachment.fileType) 
      : isImageFile(attachment.fileName);
    
    // Ensure the file URL has the correct format
    const fileUrl = getCorrectFileUrl(attachment.fileUrl);
    
    return (
      <div 
        key={attachment._id} 
        className="border rounded-lg overflow-hidden shadow-sm flex flex-col"
      >
        <div className="relative p-2 h-24 flex items-center justify-center bg-gray-50">
          {isImage ? (
            <div 
              className="w-full h-full bg-center bg-contain bg-no-repeat cursor-pointer"
              style={{ backgroundImage: `url(${fileUrl})` }}
              onClick={() => setPreviewImage(fileUrl)}
            />
          ) : (
            <div className="text-4xl text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          )}
        </div>
        <div className="p-2">
          <p className="text-sm font-medium truncate" title={attachment.fileName}>
            {attachment.fileName}
          </p>
          <div className="flex mt-2 justify-between">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={() => window.open(fileUrl, '_blank')}
            >
              View
            </button>
            <button
              className="text-xs text-red-600 hover:text-red-800"
              onClick={() => handleDeleteAttachment(attachment._id)}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add renderStepContent function to display content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Lead Info
      return (
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">NEW LEAD</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>Job Name</RequiredLabel>
                <input
                  type="text"
                  className={`${inputClasses} ${!formData.leadContact.name ? 'border-red-300' : ''}`}
                  placeholder="Enter job name"
                  value={formData.leadContact.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    leadContact: {
                      ...prev.leadContact,
                      name: e.target.value
                    }
                  }))}
                />
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>Job Address</RequiredLabel>
                <textarea
                  className={`${textAreaClasses} ${!formData.siteContact.address.street ? 'border-red-300' : ''}`}
                  placeholder="Enter job address"
                  value={formData.siteContact.address.street}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    siteContact: {
                      ...prev.siteContact,
                      address: {
                        ...prev.siteContact.address,
                        street: e.target.value
                      }
                    }
                  }))}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>City</RequiredLabel>
                <SearchableDropdown
                  options={cityOptions}
                  value={cityOptions.find(opt => opt.name === formData.address.city) || ''}
                  onChange={handleLeadCityChange}
                  getOptionLabel={(option) => option.label || ''}
                  placeholder="Search city..."
                  className={`${inputClasses} ${!formData.address.city ? 'border-red-300' : ''}`}
                />
              </div>
            </div>
          </div>
        );

      case 2: // Client Info
        return (
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">CLIENT INFORMATION</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {/* Add Client Selector Dropdown */}
              <div className="col-span-1 md:col-span-2 mb-2">
                <label className={labelClasses}>Choose from existing client</label>
                <ClientSearchSelect
                  clients={clients}
                  value={formData.client?._id || lead?.client?._id || ''}
                  onChange={handleClientSelect}
                  className={inputClasses}
                />
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>Client Name</RequiredLabel>
                <input
                  type="text"
                  className={`${inputClasses} ${!formData.client.name ? 'border-red-300' : ''}`}
                  placeholder="Enter client name"
                  value={formData.client.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    client: {
                      ...prev.client,
                      name: e.target.value
                    }
                  }))}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className={labelClasses}>Client Address</label>
                <textarea
                  className={textAreaClasses}
                  placeholder="Enter client address"
                  value={formData.client.address.street}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    client: {
                      ...prev.client,
                      address: {
                        ...prev.client.address,
                        street: e.target.value
                      }
                    }
                  }))}
                />
              </div>

              <div>
                <label className={labelClasses}>Client Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.client.cellPhone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    client: {
                      ...prev.client,
                      cellPhone: e.target.value
                    }
                  }))}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center justify-between">
                  <RequiredLabel>Client Email</RequiredLabel>
                  <span className="text-xs text-gray-500">Type email and press Enter or comma to add</span>
                </div>
                <EmailTagInput
                  emails={formData.client.emails}
                  onChange={(emails) => {
                    const emailsJoined = emails.join(', ');
                    setFormData(prev => ({
                      ...prev,
                      client: {
                        ...prev.client,
                        emails: emails,
                        email: emailsJoined // Keep email field in sync for backward compatibility
                      },
                      // Also update leadContact email to keep it in sync
                      leadContact: {
                        ...prev.leadContact,
                        email: emailsJoined
                      }
                    }));
                  }}
                  placeholder="Enter email address(es)"
                  required
                  className={!formData.client.emails.length ? 'border-red-300' : ''}
                />
              </div>
            </div>
          </div>
        );
      
      case 3: // Project/Contact Info
        return (
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">PROJECT/CONTACT INFO</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              
              <div className="mb-4 md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="same-site-as-client-full"
                    checked={sameSiteAsClient}
                    onChange={() => setSameSiteAsClient(!sameSiteAsClient)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="same-site-as-client-full" className="ml-2 text-sm text-gray-700">
                    Same as client information
                  </label>
                </div>
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <label className={labelClasses}>Contact Name</label>
                <input
                  type="text"
                  className={inputClasses}
                  placeholder="Enter contact name"
                  value={formData.siteContact.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    siteContact: {
                      ...prev.siteContact,
                      name: e.target.value
                    }
                  }))}
                  disabled={sameSiteAsClient}
                />
              </div>
              
              <div>
                <label className={labelClasses}>Contact Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.siteContact.cellPhone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    siteContact: {
                      ...prev.siteContact,
                      cellPhone: e.target.value
                    }
                  }))}
                  disabled={sameSiteAsClient}
                />
              </div>
              
              <div>
                <label className={labelClasses}>Contact Email</label>
                <input
                  type="email"
                  className={inputClasses}
                  placeholder="Enter email address"
                  value={formData.siteContact.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    siteContact: {
                      ...prev.siteContact,
                      email: e.target.value
                    },
                    // Also update leadContact email to keep it in sync
                    leadContact: {
                      ...prev.leadContact,
                      email: e.target.value
                    }
                  }))}
                  disabled={sameSiteAsClient}
                />
              </div>
            </div>
          </div>
        );
      
      case 4: // Billing Info
        return (
          <div>
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">BILLING INFORMATION</h2>
            
            <div className="mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="same-billing-as-client-full"
                  checked={sameBillingAsClient}
                  onChange={() => setSameBillingAsClient(!sameBillingAsClient)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="same-billing-as-client-full" className="ml-2 text-sm text-gray-700">
                  Same as client information
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="col-span-1 md:col-span-2">
                <label className={labelClasses}>Billing Name</label>
                <input
                  type="text"
                  className={inputClasses}
                  placeholder="Enter billing name"
                  value={formData.billingContact.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billingContact: {
                      ...prev.billingContact,
                      name: e.target.value
                    }
                  }))}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className={labelClasses}>Billing Address</label>
                <textarea
                  className={textAreaClasses}
                  placeholder="Enter billing address"
                  value={formData.billingContact.address.street}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billingContact: {
                      ...prev.billingContact,
                      address: {
                        ...prev.billingContact.address,
                        street: e.target.value
                      }
                    }
                  }))}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Billing Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.billingContact.phone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billingContact: {
                      ...prev.billingContact,
                      phone: e.target.value
                    }
                  }))}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Billing Email Address</label>
                <input
                  type="email"
                  className={inputClasses}
                  placeholder="Enter billing email address"
                  value={formData.billingContact.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billingContact: {
                      ...prev.billingContact,
                      email: e.target.value
                    }
                  }))}
                  disabled={sameBillingAsClient}
                />
              </div>
            </div>
          </div>
        );

      case 5: // Notes & Files
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">PROJECT MANAGER(S)</h2>
              <div className="col-span-1 md:col-span-2">
                <RequiredLabel>Project Manager(s)</RequiredLabel>
                <MultiSelectDropdown
                  selectedManagers={selectedManagers}
                  onManagerSelect={handleManagerSelect}
                  onManagerRemove={handleManagerRemove}
                />
                {selectedManagers.length === 0 && 
                  <p className="text-red-500 text-xs mt-1">Project manager is required</p>
                }
              </div>
            </div>
            
            <div className="mb-6">
              <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">NOTES</h2>
              <textarea
                className={`${textAreaClasses} h-32`}
                placeholder="Enter any additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  notes: e.target.value
                }))}
              />
            </div>
            
            <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">Attachments</h2>
            
            {/* Show existing attachments if any */}
            {formData.attachments && formData.attachments.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Attachments:</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {formData.attachments.map((attachment) => (
                    renderAttachment(attachment)
                  ))}
                </div>
              </div>
            )}

            {/* Upload area with drag and drop */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add new attachments
              </label>
              <div 
                ref={generalTabDropAreaRef}
                className={`border-2 border-dashed ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} rounded-lg p-6 relative`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload-general-tab"
                />
                <label htmlFor="file-upload-general-tab" className="cursor-pointer flex flex-col items-center justify-center absolute inset-0 p-6">
                  <svg className={`w-12 h-12 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="mt-2 text-sm text-gray-600">{isDragging ? 'Drop files here' : 'Drag and drop files here or click to browse'}</span>
                </label>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Add these useEffect hooks after the other useEffect hooks
  // Add effect to copy client info to billing when checkbox is toggled
  useEffect(() => {
    if (sameBillingAsClient) {
      setFormData(prev => ({
        ...prev,
        billingContact: {
          ...prev.billingContact,
          name: prev.client.name,
          phone: prev.client.cellPhone,
          email: prev.client.email,
          address: {
            street: prev.client.address.street,
            city: prev.client.address.city,
            state: prev.client.address.state,
            zipCode: prev.client.address.zipCode
          }
        }
      }));
    }
  }, [sameBillingAsClient]);

  // Add effect to copy client info to site contact when checkbox is toggled
  useEffect(() => {
    if (sameSiteAsClient) {
      // When the checkbox is toggled on, copy only contact details but NOT address
      // This preserves the job address which should be separate from client address
      setFormData(prev => ({
        ...prev,
        siteContact: {
          ...prev.siteContact,
          name: prev.client.name,
          cellPhone: prev.client.cellPhone,
          altPhone: prev.client.altPhone,
          email: prev.client.email,
          // Deliberately NOT copying address fields to preserve job address
        }
      }));
    }
  }, [sameSiteAsClient]);

  // Transform cities for dropdown with debugging
  const cityOptions = React.useMemo(() => {
    console.log('Transforming cities to options:', cities);
    return cities.map(city => ({
      label: city.name,
      value: city.code,
      name: city.name,
      state: city.state,
      code: city.code
    }));
  }, [cities]);

  // Update the city selection handlers
  const handleLeadCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        city: selectedCity?.name || '',
        state: selectedCity?.state || ''
      }
    }));
  };

  const handleClientCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      client: {
        ...prev.client,
        address: {
          ...prev.client.address,
          city: selectedCity?.name || '',
          state: selectedCity?.state || ''
        }
      }
    }));
  };

  const handleSiteContactCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      siteContact: {
        ...prev.siteContact,
        address: {
          ...prev.siteContact.address,
          city: selectedCity?.name || '',
          state: selectedCity?.state || ''
        }
      }
    }));
  };

  // Update useEffect to fetch cities
  useEffect(() => {
    if (isOpen) {
      console.log('Fetching cities...');
      dispatch(fetchCities())
        .unwrap()
        .then(result => {
          console.log('Cities fetched successfully:', result);
        })
        .catch(error => {
          console.error('Error fetching cities:', error);
        });
    }
  }, [isOpen, dispatch]);

  // Add debugging useEffect
  useEffect(() => {
    console.log('Current cities state:', cities);
    console.log('Cities loading:', citiesLoading);
    console.log('Cities error:', citiesError);
  }, [cities, citiesLoading, citiesError]);

  // Add the renderDocumentsTab function before the return statement
  const renderDocumentsTab = () => {
    const getFileIcon = (fileName) => {
      const fileType = fileName.split('.').pop().toLowerCase();
      
      if (isPdfFile(fileName)) {
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      } else if (['doc', 'docx'].includes(fileType)) {
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      } else if (['xls', 'xlsx', 'csv'].includes(fileType)) {
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      } else {
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      }
    };
    
    const isImage = (fileName) => {
      return isImageFile(fileName);
    };

    return (
      <div className="p-4">
        <h2 className="text-xl font-medium mb-6">Documents</h2>
        
        {/* File upload section */}
        <div className="mb-8">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              id="file-upload-documents"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="file-upload-documents"
              className="cursor-pointer inline-flex flex-col items-center"
            >
              <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-gray-600">
                <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
              </span>
              <span className="text-xs text-gray-500 mt-1">
                PDF, Word, Excel, JPG, PNG (max 10MB per file)
              </span>
            </label>
          </div>
        </div>
        
        {/* Document list grid */}
        {formData.attachments && formData.attachments.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {formData.attachments.map((attachment) => (
              <div 
                key={attachment._id} 
                className="border rounded-lg overflow-hidden shadow-sm flex flex-col bg-white hover:shadow-md transition-all duration-200"
              >
                <div className="relative p-2 h-32 flex items-center justify-center bg-gray-50 cursor-pointer"
                     onClick={() => {
                       const fileUrl = getCorrectFileUrl(attachment.fileUrl);
                       if (isImage(attachment.fileName)) {
                         setPreviewImage(fileUrl);
                       } else {
                         window.open(fileUrl, '_blank');
                       }
                     }}>
                  {isImage(attachment.fileName) ? (
                    <div 
                      className="w-full h-full bg-center bg-contain bg-no-repeat"
                      style={{ backgroundImage: `url(${getCorrectFileUrl(attachment.fileUrl)})` }}
                    />
                  ) : (
                    getFileIcon(attachment.fileName)
                  )}
                </div>
                <div className="p-3 flex-grow">
                  <p className="text-sm font-medium text-gray-800 truncate" title={attachment.fileName}>
                    {attachment.fileName || 'Unnamed File'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                                          {attachment.uploadDate ? new Date(attachment.uploadDate).toLocaleString('en-US', {
                        timeZone: 'America/Los_Angeles',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      }) : 'No date'}
                  </p>
                </div>
                <div className="px-3 pb-3 mt-auto flex justify-between">
                  <button
                    onClick={() => window.open(getCorrectFileUrl(attachment.fileUrl), '_blank')}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleDeleteAttachment(attachment._id)}
                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No documents uploaded yet
          </div>
        )}
      </div>
    );
  };

  // Add this useEffect to reset tab selection when dialog opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('General');
    }
  }, [isOpen]);

  return (
    <>
      {/* Replace the WideDialog with our own maximizable dialog implementation */}
      {isOpen && (
        <div className={`fixed inset-0 z-50 overflow-hidden bg-white ${isMaximized ? '' : 'mx-auto my-16 max-w-7xl rounded-lg shadow-xl'}`}>
          {/* Dialog header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Lead Details{lead?.jobNumber ? ` - ${lead.jobNumber}` : ''}</h2>
            <div className="flex items-center space-x-2">
              {/* View toggle icon */}
              {activeTab === 'General' && (
                <button 
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                  onClick={toggleView}
                  title={isTabbedView ? "Switch to full view" : "Switch to tabbed view"}
                >
                  {isTabbedView ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  )}
                </button>
              )}
              
              {/* Maximize/restore icon */}
              <button 
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                onClick={toggleMaximize}
                title={isMaximized ? "Restore dialog size" : "Maximize dialog"}
              >
                {isMaximized ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
              
              {/* Close icon */}
              <button 
                className="text-gray-400 hover:text-gray-500"
                onClick={onClose}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 overflow-hidden">
            <div className="border-b overflow-x-auto">
              <nav className="flex gap-4 px-4 min-w-max">
                {['General', 'Proposals', 'Documents', 'Permits'].map((tab) => (
                  <button
                    key={tab}
                    className={`py-4 px-2 border-b-2 font-medium ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-4 h-[calc(100vh-160px)] overflow-y-auto">
              {activeTab === 'General'
                ? renderGeneralTab()
                : activeTab === 'Proposals'
                  ? renderProposalsTab()
                  : activeTab === 'Documents'
                    ? renderDocumentsTab()
                    : (lead?._id
                      ? (
                        <PermitsTab
                          projectId={linkedProject ? linkedProject._id : lead._id}
                          key={linkedProject ? linkedProject._id : lead._id}
                        />
                      )
                      : null)
              }
            </div>

            {/* {activeTab !== 'General' && (
              <div className="flex flex-col sm:flex-row justify-end gap-2 p-4 border-t">
                <button
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Delete lead
                </button>
                <button
                  onClick={handleSave}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save & Close
                </button>
              </div>
            )} */}
          </div>
        </div>
      )}

      {/* Add back the CreateProposalDialog */}
      {showCreateProposal && (
        <CreateProposalDialog
          isOpen={showCreateProposal}
          onClose={() => {
            setShowCreateProposal(false);
            setEditingProposal(null);
            // Refresh proposals list after dialog closes
            if (lead?._id) {
              dispatch(fetchProposals(lead._id));
            }
          }}
          lead={lead}
          editingProposal={editingProposal}
        />
      )}

      <ImagePreviewDialog
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Lead
            </h3>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete this lead? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Proposal Confirmation Dialog */}
      {showDeleteProposalConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Proposal
            </h3>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete this proposal? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  setShowDeleteProposalConfirm(false);
                  setProposalToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg"
                onClick={confirmDeleteProposal}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeadDetailsDialog;